import { fabric } from 'fabric';
import EventPublisher from '../event_publisher';
import {
  POSITION_SIZE,
  POSITION_FILL_COLOR,
  POSITION_FILL_COLOR_LIGHT,
  POSITION_BOOKED_COLOR,
  POSITION_STROKE_WIDTH,
  FONT_FAMILY,
  FONT_SIZE,
  FONT_WEIGHT,
  CELL_SIZE,
  TOP_PADDING,
} from './render_properties';

export default class PostionRenderer {
  constructor(position, readOnly = false) {
    this.position = position;
    this.readOnly = readOnly;
    this.topPadding = readOnly ? TOP_PADDING : 0;
    let positionFillColor = readOnly ? POSITION_FILL_COLOR_LIGHT : POSITION_FILL_COLOR;
    this.fill = position.booked ? POSITION_BOOKED_COLOR : positionFillColor;
  }

  setCanvas(canvas) {
    this.canvas = canvas;
    return this;
  }

  render() {
    const label = this.renderLabel();
    this.shape = this.renderShape();
    this.renderedElement = this.groupElements(label, this.shape);
  }

  reRender() {
    this.remove();
    this.render();
    this.canvas.setActiveObject(this.renderedElement);
    this.canvas.fire('object:modified');
  }

  remove() {
    this.canvas.remove(this.renderedElement);
  }

  groupElements(label, shape) {
    const group = new fabric.Group([label, shape], {
      padding: 10,
      width: CELL_SIZE,
      height: CELL_SIZE,
      left: this.position.x * CELL_SIZE,
      top: (this.position.y * CELL_SIZE) + this.topPadding,
      lockMovementX: this.readOnly, lockMovementY: this.readOnly
    });
    group.on('selected', this.onSelected);
    group.on('moving', this.onMoving);
    group.on('modified', this.onModified);
    group.on('selection:moved', this.onSelectionMoved);
    group.on('selection:created', this.onSelectionCreated);
    group.hasControls = false;
    this.canvas.add(group);
    return group;
  }

  onModified = event => {
    if (event.action === 'drag') {
      this.onMoved(event)
    }
  }

  onSelected = () => {
    EventPublisher.publish('position-selected', { position: this.position });
  }

  onSelectionCreated = () => {
    this.initialX = this.position.x;
    this.initialY = this.position.y;
  }

  onSelectionMoved = event => {
    const { leftOffset, topOffset } = event;
    this.position.x = (leftOffset / CELL_SIZE) + this.initialX;
    this.position.y = (topOffset / CELL_SIZE) + this.initialY;
    EventPublisher.publish('object-moved', { object: this.position });
  }

  onMoving = () => {
    const { left, top } = this.renderedElement;
    this.position.x = left / CELL_SIZE;
    this.position.y = top / CELL_SIZE;
    EventPublisher.publish('object-moving', { object: this.position });
  }

  onMoved = () => {
    EventPublisher.publish('object-moved', { object: this.position });
  }

  renderLabel() {
    const text = new fabric.Text(this.position.identifier, {
      fontSize: FONT_SIZE,
      fontFamily: FONT_FAMILY,
      fontWeight: FONT_WEIGHT,
      selectable: false,
      originX: 'center',
      originY: 'center',
      top: -35
    });
    return text;
  }

  renderSquare() {
    return new fabric.Rect(this.getPositionDefaultArgs());
  }

  renderCircle() {
    return new fabric.Circle({
      ...this.getPositionDefaultArgs(),
      radius: POSITION_SIZE / 2,
    });
  }

  renderTriangle() {
    return new fabric.Triangle(
      this.getPositionDefaultArgs(POSITION_SIZE, Math.sqrt(3) * POSITION_SIZE / 2)
    );
  }

  renderDiamond() {
    const width = POSITION_SIZE / Math.sqrt(2);
    const height = POSITION_SIZE / Math.sqrt(2);
    return new fabric.Rect({
      ...this.getPositionDefaultArgs(width, height),
      angle: 45,
      centeredRotation: true,
    });
  }

  getPositionDefaultArgs(width = POSITION_SIZE, height = POSITION_SIZE) {
    return {
      width: width,
      height: height,
      selectable: false,
      stroke: this.position.highlight,
      strokeWidth: POSITION_STROKE_WIDTH,
      fill: this.fill,
      originX: 'center',
      originY: 'center',
    }
  }

  renderShape() {
    const renderers = {
      square: this.renderSquare(),
      triangle: this.renderTriangle(),
      diamond: this.renderDiamond(),
    };
    return renderers[this.position.shape] || this.renderCircle();
  }

  setActive() {
    this.canvas.setActiveObject(this.renderedElement);
  }

  setFillColor(fill) {
    this.fill = fill;
    this.shape.set({ fill: fill });
    this.canvas.requestRenderAll();
  }
}
